import { useRef, useState } from 'react';
import {
  Box, Button, Typography, CircularProgress, Chip, Avatar,
} from '@mui/material';
import { CloudUpload, Image as ImageIcon } from '@mui/icons-material';
import { uploadSettingFile } from '../../services/platform/settingsService';
import { resolveAssetUrl } from '../../utils/assetUrl';

type Props = {
  category: string;
  settingKey: string;
  label: string;
  description?: string;
  value: string;
  accept?: string;
  onChange: (url: string) => void;
  onUploaded?: (storage: 'local' | 's3') => void;
};

export default function SettingFileUpload({
  category,
  settingKey,
  label,
  description,
  value,
  accept = 'image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon',
  onChange,
  onUploaded,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [storage, setStorage] = useState<'local' | 's3' | null>(null);

  const previewUrl = resolveAssetUrl(value);

  const handleFile = async (file?: File) => {
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const result = await uploadSettingFile(file, category, settingKey);
      onChange(result.url);
      setStorage(result.storage);
      onUploaded?.(result.storage);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || 'Upload failed';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} gutterBottom>{label}</Typography>
      {description && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {description}
        </Typography>
      )}

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        {previewUrl ? (
          <Avatar
            key={previewUrl}
            src={previewUrl}
            variant="rounded"
            sx={{ width: 72, height: 72, bgcolor: 'grey.100' }}
          >
            <ImageIcon />
          </Avatar>
        ) : (
          <Box
            sx={{
              width: 72, height: 72, borderRadius: 2, border: 1, borderColor: 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.50',
            }}
          >
            <ImageIcon color="disabled" />
          </Box>
        )}

        <Box>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? 'Uploading...' : value ? 'Replace file' : 'Upload file'}
          </Button>
          {storage && (
            <Chip
              size="small"
              label={storage === 's3' ? 'Stored on S3' : 'Stored locally'}
              color={storage === 's3' ? 'primary' : 'default'}
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      </Box>

      {value && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1} sx={{ wordBreak: 'break-all' }}>
          {value}
        </Typography>
      )}
      {error && (
        <Typography variant="caption" color="error" display="block" mt={0.5}>{error}</Typography>
      )}
    </Box>
  );
}
